import Logout from '@/assets/member-assets/homepage/Logout_Home.svg';
import { UserAvatar } from '@/components/common/UserAvatar';
import { PiiMask } from '@/components/PiiMask';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SidebarMode } from '@/config/sidebar-config';
import { cn } from '@/lib/utils';
import { SIDEBAR_TOOLTIP_CLASSES } from './types';

interface SidebarProfileProps {
  displayName: string;
  subtitle: string;
  avatarUsername?: string | null;
  avatarSrc?: string | null;
  sidebarMode?: SidebarMode;
  isActive?: boolean;
  onProfileClick: () => void;
  onLogoutClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant: 'desktop' | 'mobile';
  testIdPrefix?: string;
}

export const SidebarProfile = ({
  displayName,
  subtitle,
  avatarUsername,
  avatarSrc,
  sidebarMode = 'full',
  isActive = false,
  onProfileClick,
  onLogoutClick,
  variant,
  testIdPrefix,
}: SidebarProfileProps) => {
  // Display name flows through PiiMask so the sidebar collapses to
  // initials when the user toggles piiMode (matches the silhouette
  // already shown by UserAvatar). The subtitle is typically a role
  // label like "Doctor" — not PII — so it stays as-is.
  const maskedDisplayName = <PiiMask kind="name" value={displayName} />;

  if (variant === 'mobile') {
    return (
      <div
        className="border-t border-gray-300 pt-5 flex items-center justify-between"
        data-testid={testIdPrefix ? `${testIdPrefix}_profileSection` : undefined}
      >
        <button
          type="button"
          className="flex items-center cursor-pointer text-left"
          onClick={onProfileClick}
          data-testid={testIdPrefix ? `${testIdPrefix}_profileBtn` : undefined}
        >
          <UserAvatar
            username={avatarUsername}
            src={avatarSrc}
            name={displayName}
            size="128"
            className="h-12 w-12"
            fallbackClassName="bg-primary/10 text-primary"
          />
          <div className="ml-3">
            <p className="text-sm font-semibold text-gray-800 truncate">{maskedDisplayName}</p>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </button>
        <button
          title="Logout"
          aria-label="Log out"
          onClick={onLogoutClick}
          className="p-2"
          data-testid={testIdPrefix ? `${testIdPrefix}_logoutBtn` : undefined}
        >
          <img src={Logout} alt="" />
        </button>
      </div>
    );
  }

  if (sidebarMode === 'icon-only') {
    return (
      <div
        className="space-y-2"
        data-testid={testIdPrefix ? `${testIdPrefix}_profileSection` : undefined}
      >
        <button
          type="button"
          aria-label={`Profile: ${displayName}`}
          className="flex justify-center cursor-pointer w-full"
          onClick={onProfileClick}
          data-testid={testIdPrefix ? `${testIdPrefix}_profileBtn` : undefined}
        >
          <UserAvatar
            username={avatarUsername}
            src={avatarSrc}
            name={displayName}
            size="128"
            className="h-10 w-10 rounded-3xl ring-2 ring-white/20"
            fallbackClassName="rounded-3xl bg-white/15 text-white"
          />
        </button>
        <button
          title="Logout"
          aria-label="Log out"
          className="w-full flex justify-center p-2 rounded-full hover:bg-white/10 transition-colors duration-150 ease motion-reduce:transition-none"
          onClick={onLogoutClick}
          data-testid={testIdPrefix ? `${testIdPrefix}_logoutBtn` : undefined}
        >
          <img src={Logout} alt="" className="w-5 h-5 brightness-0 invert opacity-50" />
        </button>
      </div>
    );
  }

  if (sidebarMode === 'compact') {
    return (
      <div
        className="flex flex-col items-center gap-1"
        data-testid={testIdPrefix ? `${testIdPrefix}_profileSection` : undefined}
      >
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Profile: ${displayName}`}
              className={cn(
                'relative p-1 rounded-full cursor-pointer transition-colors duration-150 ease motion-reduce:transition-none',
                isActive
                  ? 'ring-2 ring-white ring-offset-2 ring-offset-primary'
                  : 'hover:ring-2 hover:ring-white/30 hover:ring-offset-1 hover:ring-offset-primary'
              )}
              onClick={onProfileClick}
              data-testid={testIdPrefix ? `${testIdPrefix}_profileBtn` : undefined}
            >
              <UserAvatar
                username={avatarUsername}
                src={avatarSrc}
                name={displayName}
                size="48"
                className="h-8 w-8"
                fallbackClassName="bg-white/15 text-white text-xs"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8} className={SIDEBAR_TOOLTIP_CLASSES}>
            <div className="space-y-0.5">
              <p className="text-[13px] font-semibold text-gray-900">{maskedDisplayName}</p>
              <p className="text-xs text-gray-500">{subtitle}</p>
            </div>
          </TooltipContent>
        </Tooltip>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <button
              aria-label="Log out"
              className="p-2.5 rounded-lg text-white/40 hover:text-white/60 hover:bg-white/10 transition-colors duration-150 ease motion-reduce:transition-none"
              onClick={onLogoutClick}
              data-testid={testIdPrefix ? `${testIdPrefix}_logoutBtn` : undefined}
            >
              <img src={Logout} alt="" className="w-4 h-4 brightness-0 invert opacity-50" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8} className={SIDEBAR_TOOLTIP_CLASSES}>
            <p className="text-[13px] font-semibold text-gray-900">Log out</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center ps-2 pt-1 pb-1 rounded-3xl transition-colors duration-150 ease motion-reduce:transition-none',
        isActive ? 'bg-white/15' : 'hover:bg-white/10'
      )}
      data-testid={testIdPrefix ? `${testIdPrefix}_profileSection` : undefined}
    >
      <button
        type="button"
        className="flex items-center flex-1 min-w-0 cursor-pointer text-left"
        onClick={onProfileClick}
        data-testid={testIdPrefix ? `${testIdPrefix}_profileBtn` : undefined}
      >
        <UserAvatar
          username={avatarUsername}
          src={avatarSrc}
          name={displayName}
          size="128"
          className="h-10 w-10 rounded-3xl flex-shrink-0 ring-2 ring-white/20"
          fallbackClassName="rounded-3xl bg-white/15 text-white"
        />
        <div className="ml-3 flex-1 overflow-hidden">
          <p className="text-sm font-semibold text-white truncate">{maskedDisplayName}</p>
          <p className="text-xs text-white/50 truncate">{subtitle}</p>
        </div>
      </button>
      <button
        title="Logout"
        aria-label="Log out"
        className="ml-2 p-2 rounded-full hover:bg-white/10 transition-colors duration-150 ease motion-reduce:transition-none"
        onClick={onLogoutClick}
        data-testid={testIdPrefix ? `${testIdPrefix}_logoutBtn` : undefined}
      >
        <img src={Logout} alt="" className="brightness-0 invert opacity-50" />
      </button>
    </div>
  );
};
